import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { PATCH_URL } from '@utils/api/endpoints.ts';
import { SessionMetaData } from '@utils/types/editors/sessions.ts';
import { AxiosError } from 'axios';

type UpdateSessionMetadataParams = {
  sessionId: string;
  metaData?: Partial<SessionMetaData>;
  mediaSource?: string | null;
  /**
   * zoom_id of a same-account session to use as the durable replacement
   * media reference. The backend re-signs playback URLs against it every
   * call so the reference outlives any pasted signed link.
   */
  mediaSourceSessionId?: string | null;
};

type UpdateSessionMetadataOptions = {
  onError?: (error: AxiosError) => void;
  onSuccess?: () => void;
};

export function useUpdateSessionMetadata(
  options?: UpdateSessionMetadataOptions
) {
  const queryClient = useQueryClient();

  return useMutation(
    ['updateSessionMetadata'],
    async ({
      sessionId,
      metaData,
      mediaSource,
      mediaSourceSessionId
    }: UpdateSessionMetadataParams) => {
      const payload: Record<string, unknown> = {};
      if (metaData !== undefined) {
        payload.meta_data = metaData;
      }
      if (mediaSource !== undefined) {
        payload.media_source = mediaSource;
      }
      if (mediaSourceSessionId !== undefined) {
        payload.media_source_session_id = mediaSourceSessionId;
      }
      return await Axios.patch(
        PATCH_URL(sessionId).updateSessionMetadata,
        payload
      );
    },
    {
      onSuccess: async (_, { sessionId }) => {
        await queryClient.refetchQueries([
          'transcript-editor-session',
          sessionId
        ]);
        options?.onSuccess?.();
      },
      onError: (error: AxiosError) => {
        options?.onError?.(error);
      }
    }
  );
}
